import React, {useContext, useEffect, useState} from 'react';
import {MY_FILES_NEW_ENABLED} from '../../app';
import {observer} from 'mobx-react-lite';
import {Outlet, useLocation} from 'react-router-dom';
import {NetworkContext} from './lib/context';
import AuthWall from '../../components/auth-wall';
import {useNavigate} from 'react-router';
import {HeaderContext} from '../../components/header/context';
import Header from '../../components/header';
import MyContent from '../../components/header/my-content/my-content';
import NetworkOptimizationErrorBoundary from './components/error-boundary';
import {ActionableNotification, InlineNotification} from '@carbon/react';
import UploadStatusChip from './components/upload-status-chip';
import UploadStatusModal from './components/upload-status-modal';
import './network-optimization.scss';

const DownloadNotifications: React.FC = observer(() => {
    const store = useContext(NetworkContext);
    const isVisible = store.isDownloading || store.downloadError !== null;
    return (
        <>
            {/* Spacer keeps content below the fixed notification so overflow menus don't overlap it */}
            {isVisible && <div style={{height: '3rem'}} aria-hidden='true' />}
            {store.isDownloading && (
                <div className='fixed top-[49px] left-0 right-0 z-[9999] download-notification-wrapper'>
                    <ActionableNotification
                        kind={store.downloadUsedFallback ? 'warning' : 'info'}
                        title='Downloading Error Report'
                        subtitle={
                            store.downloadUsedFallback
                                ? 'Large files may not complete in this browser. Use Chrome or Edge for best results.'
                                : 'Your file is being saved to disk.'
                        }
                        actionButtonLabel='Cancel'
                        onActionButtonClick={() => store.cancelDownload?.()}
                        hideCloseButton
                        style={{maxWidth: '100%'}}
                    />
                </div>
            )}
            {store.downloadError !== null && (
                <div className='fixed top-[49px] left-0 right-0 z-[9999]'>
                    <InlineNotification
                        kind='error'
                        title='Download Failed'
                        subtitle={store.downloadError}
                        onCloseButtonClick={() => store.clearDownloadState()}
                        style={{maxWidth: '100%'}}
                    />
                </div>
            )}
        </>
    );
});

const NetworkOptimizationViewPage: React.FC = observer(() => {
    const networkOptimizationStore = useContext(NetworkContext);
    const headerStore = useContext(HeaderContext);
    const navigate = useNavigate();
    const location = useLocation();
    const [errorBoundaryKey, setErrorBoundaryKey] = useState(0);
    const [hasError, setHasError] = useState(false);
    const [statusModalOpen, setStatusModalOpen] = useState(false);

    const isNetworkOptimization = location.pathname.includes('/network-optimization');
    headerStore.useNewDesign = true;
    
    const VALID_PAGE_NAMES = [
        'network-analysis',
        'reports',
        'generate-report',
        'compare'
    ];

    const isInvalidPath = () => {
        const pathParts = location.pathname.split('/').filter(Boolean);

        // Check if path starts with network-optimization
        if (pathParts[0] !== 'network-optimization') return true;

        // Only allow /network-optimization/my-files or full pattern
        if (pathParts.length === 1) {
            return true; // Reject /network-optimization alone
        }

        const validFilesPaths = MY_FILES_NEW_ENABLED ? ['my-files', 'my-files-new'] : ['my-files'];
        if (pathParts.length === 2 && !validFilesPaths.includes(pathParts[1])) {
            return true;
        }

        // Check for pattern: /network-optimization/:rosterId/:revisionId/:pageName
        if (pathParts.length === 4) {
            const rosterId = pathParts[1];
            const revisionId = pathParts[2];
            const pageName = pathParts[3];

            if (rosterId === 'null' || rosterId === 'NaN' || revisionId === 'null' || revisionId === 'NaN') {
                return true;
            }

            if (!VALID_PAGE_NAMES.includes(pageName)) {
                return true;
            }
        }

        return false;
    };
    useEffect(() => {
        document.title = 'Network Optimizer';
        if (isInvalidPath()) {
            navigate('/network-optimization/my-files');
        }
        if (networkOptimizationStore.navItems.length === 0) {
            networkOptimizationStore.loadHeaders();
        }

        headerStore.hideExtraButtons = isNetworkOptimization;
    }, [navigate, location.pathname, headerStore, isNetworkOptimization]);

    useEffect(() => {
        networkOptimizationStore.restoreUploadJobs();
        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            if (networkOptimizationStore.hasActiveUploads) {
                e.preventDefault();
                e.returnValue = '';
            }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [networkOptimizationStore]);

    useEffect(() => {
        // Reset error boundary on navigation only if there's an error
        if (hasError) {
            setErrorBoundaryKey(prev => prev + 1);
            setHasError(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location]);

    const hasRosterSelected = networkOptimizationStore.runId !== null;
    const DISABLED_TOOLTIP = 'Please select a roster on the My Files tab to enable this feature.';

    const headerNavItems = networkOptimizationStore.navItems
        .filter(item => !item.hideTab)
        .map((item) => ({
            label: item.label,
            path: `${item.useRosterID ? `${networkOptimizationStore.runId}/${networkOptimizationStore.revisionId}/` : ''}${item.path}`,
            disabled: item.useRosterID && !hasRosterSelected,
            disabledTooltip: item.useRosterID && !hasRosterSelected ? DISABLED_TOOLTIP : undefined
        }));

    return (
        <AuthWall>
            <NetworkContext.Provider value={networkOptimizationStore}>
                <div className="network-optimization-page">
                    <header className="header">
                        <Header logoLink="/network-optimization/my-files" siteNameOverride="Network Optimization" showLogo={false} showOrg navItems={headerNavItems} hideProfileLinks/>
                    </header>
                    <DownloadNotifications />
                    <NetworkOptimizationErrorBoundary key={errorBoundaryKey} onError={setHasError}>
                        <Outlet />
                    </NetworkOptimizationErrorBoundary>
                    {headerStore.showMyContent && <div className="fixed right-0 top-0 h-screen w-1/5 z-[100]">
                        <MyContent />
                    </div>}
                    <UploadStatusChip onOpen={() => setStatusModalOpen(true)} />
                    <UploadStatusModal
                        open={statusModalOpen}
                        onClose={() => setStatusModalOpen(false)}
                    />
                </div>
            </NetworkContext.Provider>
        </AuthWall>
    );
});
export default NetworkOptimizationViewPage;